/**
 * AudioBus - mixing bus in the audio graph. Wraps a WebAudio GainNode for volume control and chaining.
 */

export class AudioBus {
  public readonly name: string;
  private gainNode: GainNode;

  constructor(name: string, context: AudioContext) {
    this.name = name;
    this.gainNode = context.createGain();
  }

  /**
   * Connects this bus to another audio node or bus.
   *
   * @param destination - The destination AudioNode or AudioBus to connect to
   */
  public connect(destination: AudioNode | AudioBus): void {
    if (destination instanceof AudioBus) {
      this.gainNode.connect(destination.getInput());
    } else {
      this.gainNode.connect(destination);
    }
  }

  /**
   * Disconnects this bus from a specific destination or all connections.
   *
   * @param destination - Optional destination to disconnect from
   */
  public disconnect(destination?: AudioNode | AudioBus): void {
    if (destination) {
      if (destination instanceof AudioBus) {
        this.gainNode.disconnect(destination.getInput());
      } else {
        this.gainNode.disconnect(destination);
      }
    } else {
      this.gainNode.disconnect();
    }
  }

  /**
   * Sets the volume of the bus.
   *
   * @param value - Volume value (0.0 to 1.0 or higher)
   * @param time - Optional time to ramp to the value
   */
  public setVolume(value: number, time?: number): void {
    if (time !== undefined) {
      this.gainNode.gain.linearRampToValueAtTime(value, time);
    } else {
      this.gainNode.gain.value = value;
    }
  }

  /** Gets the current volume of the bus. */
  public getVolume(): number {
    return this.gainNode.gain.value;
  }

  /** Gets the underlying WebAudio input node. Use this to connect sources to this bus. */
  public getInput(): AudioNode {
    return this.gainNode;
  }
}
